/**
 * Chip „In Akte speichern“ (`save_to_file`) — Vertrag 1.
 * Rein, ohne React: Speichern in boundAkte/DOCX, nie MEMCORE/BRAIN.
 * Dist-Gate grept PORTAL_AKTE_CHIP_SINK_MARKER (Vite behält das String-Literal).
 */

export type ArkoQuickActionType =
  | 'lookup_law'
  | 'ogh_dazu'
  | 'web_recherche'
  | 'export_pdf'
  | 'save_to_file'
  | 'refine_letter'
  | 'schreiben_erstellen'
  | 'pick_variant'
  | 'variant_1'
  | 'variant_2'
  | 'variant_3';

export interface SuggestedAction {
  action: ArkoQuickActionType;
  label: string;
  /** Zweite Aufgabe. Der Klick schickt nur diesen Text, nicht die ganze Nachricht. */
  followupQuery?: string;
}

export const SAVE_TO_FILE_CHIP_LABEL = 'In Akte speichern';
export const MEMCORE_SAVE_LABEL = 'Ins Gedächtnis';

/** Überlebt Minify — UAT-Gate / Dist-Fingerprint. */
export const PORTAL_AKTE_CHIP_SINK_MARKER = 'akte-chip-sink:bound_akte';

export const ARKO_QUICK_ACTION_TYPES: ArkoQuickActionType[] = [
  'lookup_law',
  'ogh_dazu',
  'web_recherche',
  'export_pdf',
  'save_to_file',
  'refine_letter',
  'schreiben_erstellen',
  'pick_variant',
  'variant_1',
  'variant_2',
  'variant_3',
];

export const ARKO_QUICK_ACTION_LABELS: Record<ArkoQuickActionType, string> = {
  lookup_law: '§§ nachschlagen',
  ogh_dazu: 'Was sagt der OGH dazu?',
  web_recherche: 'Im Internet recherchieren',
  export_pdf: 'Als PDF',
  save_to_file: SAVE_TO_FILE_CHIP_LABEL,
  refine_letter: 'Schreiben anpassen',
  schreiben_erstellen: 'Schreiben erstellen',
  pick_variant: 'Welche Variante?',
  variant_1: 'Variante 1',
  variant_2: 'Variante 2',
  variant_3: 'Variante 3',
};

const VARIANT_CHIP_ACTIONS: ArkoQuickActionType[] = ['variant_1', 'variant_2', 'variant_3'];

export type ArkoChipSink =
  | 'bound_akte'
  | 'pdf'
  | 'law'
  | 'judicature'
  | 'web'
  | 'refine'
  | 'variant'
  | 'unknown';

/** MEMCORE ist absichtlich kein Chip-Sink — eigener Button, anderes Label. */
export function chipActionSink(action: string): ArkoChipSink {
  switch (action) {
    case 'save_to_file':
      return 'bound_akte';
    case 'export_pdf':
      return 'pdf';
    case 'lookup_law':
      return 'law';
    case 'ogh_dazu':
      return 'judicature';
    case 'web_recherche':
      return 'web';
    case 'refine_letter':
      return 'refine';
    case 'schreiben_erstellen':
      return 'refine';
    case 'variant_1':
    case 'variant_2':
    case 'variant_3':
    case 'pick_variant':
      return 'variant';
    default:
      return 'unknown';
  }
}

export type SaveToFileChipSink = 'bound_akte_docx' | 'prompt_open_akte' | 'hidden';

export function resolveSaveToFileChipSink(opts: {
  akteBound: boolean;
  hasDocxExport: boolean;
}): SaveToFileChipSink {
  if (!opts.hasDocxExport) return 'hidden';
  if (!opts.akteBound) return 'prompt_open_akte';
  return 'bound_akte_docx';
}

export function saveToFileChipUsesMemcore(): boolean {
  return chipActionSink('save_to_file') === ('memcore' as ArkoChipSink);
}

export function parseSuggestedActions(raw: unknown): SuggestedAction[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<ArkoQuickActionType>();
  const out: SuggestedAction[] = [];
  const pushAction = (action: ArkoQuickActionType, label?: string, followupQuery?: unknown) => {
    if (!ARKO_QUICK_ACTION_TYPES.includes(action) || seen.has(action)) return;
    if (action === 'pick_variant') {
      for (const v of VARIANT_CHIP_ACTIONS) pushAction(v);
      return;
    }
    seen.add(action);
    const canonical = ARKO_QUICK_ACTION_LABELS[action];
    const followup = typeof followupQuery === 'string' ? followupQuery.trim().slice(0, 500) : '';
    out.push({
      action,
      label: action === 'save_to_file' ? canonical : ((label || '').trim() || canonical),
      ...(followup ? { followupQuery: followup } : {}),
    });
  };
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const action = String(o.action ?? '') as ArkoQuickActionType;
    pushAction(action, String(o.label ?? ''), o.followupQuery);
  }
  return out;
}
